import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

// =====================================================================================
// Modèles
// =====================================================================================

export interface ProspectTrigger {
  type: string;
  date?: string;
  libelle?: string;
  source?: string;
  confiance?: string;
  nb_offres?: number;
}

export interface ProspectContactMini {
  id: number;
  prenom?: string;
  nom?: string;
  fonction?: string;
  persona?: string;
  linkedin_url?: string;
  email_pro?: string;
  email_statut?: string;
  consentement_appel: boolean;
  opposition: boolean;
  a_repondu: boolean;
  sequence_etape: number;
}

export interface ProspectCompany {
  id: number;
  siren: string;
  siret_siege?: string;
  raison_sociale: string;
  nom_commercial?: string;
  forme_juridique?: string;
  date_creation?: string;
  etat_administratif?: string;

  naf_code?: string;
  naf_libelle?: string;
  secteur?: string;

  tranche_effectif?: string;
  effectif_min?: number;
  effectif_max?: number;

  adresse?: string;
  code_postal?: string;
  ville?: string;
  departement?: string;

  telephone_standard?: string;
  site_web?: string;
  linkedin_url?: string;

  dirigeant_nom?: string;
  dirigeant_prenom?: string;
  dirigeant_qualite?: string;

  score: number;
  score_detail?: { [cle: string]: any };
  produit_cible?: string;

  triggers?: ProspectTrigger[];
  dernier_trigger_at?: string;

  statut: string;
  source: string;
  campagne?: string;
  notes?: string;
  user_id?: number;
  contact_id?: number;
  converted_at?: string;
  created_at?: string;
  contacts: ProspectContactMini[];
}

export interface ProspectContact {
  id: number;
  company_id: number;
  civilite?: string;
  prenom?: string;
  nom?: string;
  fonction?: string;
  persona?: string;
  linkedin_url?: string;
  email_pro?: string;
  email_statut?: string;
  telephone_pro?: string;
  sequence_etape: number;
  sequence_variante?: string;
  informe_rgpd_at?: string;
  consentement_appel: boolean;
  consentement_source?: string;
  consentement_at?: string;
  opposition: boolean;
  a_repondu: boolean;
  appel_autorise: boolean;
  prochaine_action_at?: string;
  notes?: string;
  raison_sociale?: string;
  telephone_standard?: string;
}

export interface CompanyFilters {
  statut?: string;
  produit_cible?: string;
  secteur?: string;
  departement?: string;
  campagne?: string;
  score_min?: number;
  score_max?: number;
  avec_trigger?: boolean;
  tri?: string;
}

export interface FunnelStats {
  prospects_en_base: number;
  travaillables: number;
  en_sequence: number;
  invitations_envoyees: number;
  invitations_acceptees: number;
  emails_envoyes: number;
  emails_bounces: number;
  reponses: number;
  reponses_positives: number;
  appels_passes: number;
  rdv_pris: number;
  convertis: number;
  oppositions: number;
}

export interface FunnelRates {
  taux_acceptation_linkedin: number;
  taux_reponse_email: number;
  taux_bounce: number;
  taux_reponse_global: number;
  taux_rdv: number;
  taux_conversion: number;
}

export interface QuotaJour {
  invitations_aujourdhui: number;
  invitations_plafond: number;
  emails_aujourdhui: number;
  emails_plafond: number;
  appels_aujourdhui: number;
}

export interface RepartitionItem {
  cle: string;
  libelle?: string;
  total: number;
}

export interface ProspectionStats {
  date_debut: string;
  date_fin: string;
  funnel: FunnelStats;
  taux: FunnelRates;
  quotas: QuotaJour;
  par_statut: RepartitionItem[];
  par_produit: RepartitionItem[];
  par_secteur: RepartitionItem[];
  par_departement: RepartitionItem[];
  alertes: string[];
}

export interface SecteurRef {
  cle: string;
  libelle: string;
  priorite: string;
  argument: string;
  nb_codes_naf: number;
}

export interface Referentiels {
  secteurs: SecteurRef[];
  statuts: string[];
  produits: string[];
  personas: string[];
  sources_consentement: string[];
  score_minimum_travaillable: number;
  url_information_rgpd: string;
}

export interface ImportResult {
  lignes_lues: number;
  crees: number;
  mis_a_jour: number;
  ignores: number;
  erreurs: string[];
}

export interface ConvertResult {
  company_id: number;
  contact_id: number;
  reference?: string;
  deja_converti: boolean;
}

// =====================================================================================
// Service
// =====================================================================================

@Injectable({ providedIn: 'root' })
export class ProspectionService {
  private apiUrl = `${environment.apiUrl}${environment.apiEndpoint}/prospection`;

  constructor(private http: HttpClient) {}

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });
  }

  private buildFilterParams(search?: string, filters?: CompanyFilters): HttpParams {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    if (!filters) {
      return params;
    }
    Object.entries(filters).forEach(([cle, valeur]) => {
      if (valeur !== null && valeur !== undefined && valeur !== '') {
        params = params.set(cle, String(valeur));
      }
    });
    return params;
  }

  // --- Entreprises ---

  listCompanies(
    limit = 50,
    offset = 0,
    search?: string,
    filters?: CompanyFilters
  ): Observable<ProspectCompany[]> {
    const params = this.buildFilterParams(search, filters)
      .set('limit', limit.toString())
      .set('offset', offset.toString());
    return this.http.get<ProspectCompany[]>(`${this.apiUrl}/companies`, {
      params,
      headers: this.getAuthHeaders(),
    });
  }

  countCompanies(search?: string, filters?: CompanyFilters): Observable<{ count: number }> {
    // `tri` n'a pas de sens pour un comptage et n'est pas accepté par l'endpoint.
    const { tri, ...reste } = filters || {};
    const params = this.buildFilterParams(search, reste as CompanyFilters);
    return this.http.get<{ count: number }>(`${this.apiUrl}/companies/count`, {
      params,
      headers: this.getAuthHeaders(),
    });
  }

  getCompany(id: number): Observable<ProspectCompany> {
    return this.http.get<ProspectCompany>(`${this.apiUrl}/companies/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  updateCompany(id: number, payload: Partial<ProspectCompany>): Observable<ProspectCompany> {
    return this.http.patch<ProspectCompany>(`${this.apiUrl}/companies/${id}`, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  rescoreCompany(id: number): Observable<ProspectCompany> {
    return this.http.post<ProspectCompany>(
      `${this.apiUrl}/companies/${id}/rescore`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

  deleteCompany(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/companies/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  convertCompany(id: number, payload: any = {}): Observable<ConvertResult> {
    return this.http.post<ConvertResult>(`${this.apiUrl}/companies/${id}/convert`, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  // --- Contacts nominatifs ---

  listContacts(params: {
    limit?: number;
    offset?: number;
    company_id?: number;
    persona?: string;
    search?: string;
    appelables?: boolean;
    a_relancer?: boolean;
  }): Observable<ProspectContact[]> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([cle, valeur]) => {
      if (valeur !== null && valeur !== undefined && valeur !== '') {
        httpParams = httpParams.set(cle, String(valeur));
      }
    });
    return this.http.get<ProspectContact[]>(`${this.apiUrl}/contacts`, {
      params: httpParams,
      headers: this.getAuthHeaders(),
    });
  }

  createContact(payload: Partial<ProspectContact>): Observable<ProspectContact> {
    return this.http.post<ProspectContact>(`${this.apiUrl}/contacts`, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  updateContact(id: number, payload: Partial<ProspectContact>): Observable<ProspectContact> {
    return this.http.patch<ProspectContact>(`${this.apiUrl}/contacts/${id}`, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  /** Enregistre la preuve écrite qui débloque le canal téléphonique. */
  enregistrerConsentement(
    contactId: number,
    source: string,
    preuve: string
  ): Observable<ProspectContact> {
    return this.http.post<ProspectContact>(
      `${this.apiUrl}/contacts/${contactId}/consentement`,
      { source, preuve },
      { headers: this.getAuthHeaders() }
    );
  }

  /** Droit d'opposition RGPD : coupe définitivement tous les canaux. */
  enregistrerOpposition(contactId: number, motif?: string): Observable<ProspectContact> {
    return this.http.post<ProspectContact>(
      `${this.apiUrl}/contacts/${contactId}/opposition`,
      { motif },
      { headers: this.getAuthHeaders() }
    );
  }

  // --- Activités ---

  logActivity(payload: {
    company_id?: number;
    contact_id?: number;
    canal: string;
    action: string;
    sequence_etape?: number;
    variante?: string;
    sentiment?: string;
    sujet?: string;
    contenu?: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/activities`, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  listActivities(companyId?: number, contactId?: number, limit = 100): Observable<any[]> {
    let params = new HttpParams().set('limit', limit.toString());
    if (companyId) {
      params = params.set('company_id', companyId.toString());
    }
    if (contactId) {
      params = params.set('contact_id', contactId.toString());
    }
    return this.http.get<any[]>(`${this.apiUrl}/activities`, {
      params,
      headers: this.getAuthHeaders(),
    });
  }

  // --- Pilotage ---

  getStats(jours = 30): Observable<ProspectionStats> {
    return this.http.get<ProspectionStats>(`${this.apiUrl}/stats`, {
      params: new HttpParams().set('jours', jours.toString()),
      headers: this.getAuthHeaders(),
    });
  }

  getReferentiels(): Observable<Referentiels> {
    return this.http.get<Referentiels>(`${this.apiUrl}/referentiels`, {
      headers: this.getAuthHeaders(),
    });
  }

  // --- Collecte / import ---

  lancerCollecte(payload: {
    departements: string[];
    secteurs?: string[];
    effectif_min?: number;
    effectif_max?: number;
    limite?: number;
    campagne?: string;
    avec_bodacc?: boolean;
    avec_france_travail?: boolean;
    persister?: boolean;
  }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/harvest`, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  importerCsv(fichier: File, campagne?: string): Observable<ImportResult> {
    const formData = new FormData();
    formData.append('file', fichier);
    let params = new HttpParams();
    if (campagne) {
      params = params.set('campagne', campagne);
    }
    // Pas de Content-Type ici : le navigateur pose lui-même la frontière multipart.
    const token = localStorage.getItem('access_token');
    return this.http.post<ImportResult>(`${this.apiUrl}/import/csv`, formData, {
      params,
      headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
    });
  }
}
